// Admin → Stripe → Webhooks / Eventos (AUDITORÍA, sólo lectura).
//
// No permite modificar, reenviar ni eliminar eventos de Stripe: todas las
// consultas son de lectura y no existe ninguna acción mutante en esta pantalla.
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, ChevronRight, ShieldCheck, Download } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  listStripeAuditEvents,
  getStripeAuditDetail,
  type StripeAuditRow,
  type StripeAuditFilters,
  type AuditHealth,
} from "@/lib/stripe-webhook-audit.functions";
import { formatStripeAmount } from "@/lib/stripe-event-summary";
import { downloadCsv } from "@/lib/csv-export";


export const Route = createFileRoute("/_authenticated/admin/stripe-webhooks")({
  head: () => ({
    meta: [
      { title: "Admin · Stripe Webhooks / Eventos — Mostrando México" },
      { name: "description", content: "Auditoría de eventos de Stripe, entrega del webhook y reservas en Bókun." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: StripeWebhooksAuditPage,
});

const PAGE_SIZE = 50;

type FormState = {
  from: string;
  to: string;
  env: "" | "sandbox" | "live";
  eventType: string;
  search: string;
  processing: "all" | "processed" | "pending";
  delivery: "all" | "ok" | "error" | "retried";
  booking: "all" | "with_booking" | "with_bokun" | "without_bokun";
  health: "all" | AuditHealth;
};

const EMPTY: FormState = {
  from: "", to: "", env: "", eventType: "", search: "",
  processing: "all", delivery: "all", booking: "all", health: "all",
};

const HEALTH_META: Record<AuditHealth, { dot: string; label: string; className: string }> = {
  ok: { dot: "🟢", label: "Procesado correctamente", className: "text-emerald-600" },
  pending: { dot: "🟡", label: "Pendiente / requiere revisión", className: "text-amber-600" },
  error: { dot: "🔴", label: "Error de entrega o procesamiento", className: "text-red-600" },
};

function toFilters(f: FormState, page: number): StripeAuditFilters {
  const iso = (v: string) => (v ? new Date(v).toISOString() : undefined);
  const txt = (v: string) => v.trim() || undefined;
  return {
    from: iso(f.from),
    to: iso(f.to),
    env: f.env || undefined,
    eventType: txt(f.eventType),
    search: txt(f.search),
    processing: f.processing,
    delivery: f.delivery,
    booking: f.booking,
    health: f.health,
    pageSize: PAGE_SIZE,
    page,
  };
}

function fmtDate(v: string | null | undefined) {
  if (!v) return "—";
  return new Date(v).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "medium" });
}

function HealthBadge({ health }: { health: AuditHealth }) {
  const m = HEALTH_META[health];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${m.className}`}>
      <span aria-hidden>{m.dot}</span>
      <span className="hidden sm:inline">{m.label}</span>
    </span>
  );
}

function HttpBadge({ status }: { status: number | null }) {
  if (status == null) return <span className="text-muted-foreground text-xs">sin registro</span>;
  const tone = status >= 400 ? "bg-red-500/10 text-red-600" : "bg-emerald-500/10 text-emerald-600";
  return <span className={`rounded px-1.5 py-0.5 text-xs font-mono ${tone}`}>{status}</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm break-all">{children ?? "—"}</div>
    </div>
  );
}

function StripeWebhooksAuditPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [applied, setApplied] = useState<FormState>(EMPTY);
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useServerFn(listStripeAuditEvents);
  const detailFn = useServerFn(getStripeAuditDetail);

  const filters = useMemo(() => toFilters(applied, page), [applied, page]);

  const query = useQuery({
    queryKey: ["stripe-audit", filters],
    queryFn: () => list({ data: filters }),
  });

  const detail = useQuery({
    queryKey: ["stripe-audit-detail", openId],
    queryFn: () => detailFn({ data: { eventId: openId! } }),
    enabled: !!openId,
  });

  const rows: StripeAuditRow[] = query.data?.rows ?? [];
  const counts = query.data?.counts;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const apply = () => {
    setPage(0);
    setApplied(form);
  };

  // Exporta EXACTAMENTE los eventos que producen los filtros aplicados,
  // recorriendo todas las páginas (sólo lectura, sin mutaciones).
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const all: StripeAuditRow[] = [];
      for (let p = 0; p < 40; p++) {
        const res = await list({ data: { ...toFilters(applied, p), pageSize: 200 } });
        all.push(...res.rows);
        if (!res.hasMore) break;
      }
      downloadCsv(
        `stripe_auditoria_${new Date().toISOString().slice(0, 10)}`,
        [
          { key: "estado", label: "Estado" },
          { key: "event_id", label: "ID del evento" },
          { key: "fecha", label: "Fecha/hora" },
          { key: "tipo", label: "Tipo de evento" },
          { key: "entorno", label: "Entorno" },
          { key: "payment_intent", label: "Payment Intent" },
          { key: "session", label: "Checkout Session" },
          { key: "monto", label: "Monto" },
          { key: "moneda", label: "Moneda" },
          { key: "cliente", label: "Cliente" },
          { key: "correo", label: "Correo" },
          { key: "intentos", label: "Intentos de entrega" },
          { key: "pending_webhooks", label: "pending_webhooks" },
          { key: "endpoint", label: "Endpoint" },
          { key: "http", label: "Código HTTP" },
          { key: "respuesta", label: "Respuesta del webhook" },
          { key: "procesado", label: "Procesado" },
          { key: "error", label: "Error de procesamiento" },
          { key: "reserva", label: "Reserva" },
          { key: "reserva_estado", label: "Estado de la reserva" },
          { key: "bokun_id", label: "Bókun ID" },
          { key: "bokun_conf", label: "Bókun confirmación" },
        ],
        all.map((r) => ({
          estado: HEALTH_META[r.health].label,
          event_id: r.stripe_event_id,
          fecha: new Date(r.received_at).toLocaleString("es-MX"),
          tipo: r.event_type ?? "",
          entorno: r.env,
          payment_intent: r.payment_intent_id ?? "",
          session: r.checkout_session_id ?? "",
          monto: r.amount != null ? formatStripeAmount(r.amount, r.currency) : "",
          moneda: (r.currency ?? "").toUpperCase(),
          cliente: r.customer_name ?? "",
          correo: r.customer_email ?? "",
          intentos: r.delivery_attempts ?? "",
          pending_webhooks: r.pending_webhooks ?? "",
          endpoint: r.endpoint_path ?? "",
          http: r.response_status ?? "",
          respuesta: r.response_body ?? "",
          procesado: r.processed_at ? new Date(r.processed_at).toLocaleString("es-MX") : "No",
          error: r.processing_error ?? "",
          reserva: r.booking?.booking_code ?? "",
          reserva_estado: r.booking?.status ?? "",
          bokun_id: r.booking?.bokun_booking_id ?? "",
          bokun_conf: r.booking?.bokun_confirmation_code ?? "",
        })),
      );
    } finally {
      setExporting(false);
    }
  };


  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto px-4 py-8 space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold">Stripe · Webhooks / Eventos</h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Auditoría de sólo lectura: confirma que cada pago de Stripe llegó al webhook,
            fue procesado por Mostrando México y terminó en una reserva de Bókun.
          </p>
          <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            Esta pantalla no permite modificar, reenviar ni eliminar eventos.
          </p>
        </header>

        <AdminNav />

        {/* Filtros */}
        <section className="rounded-lg border p-4 grid gap-3 md:grid-cols-4">
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="q">Buscar (evento, pago, sesión, cliente)</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="q" className="pl-8" value={form.search}
                placeholder="evt_… / pi_… / cs_… / correo"
                onChange={(e) => set("search", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && apply()}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="from">Desde</Label>
            <Input id="from" type="datetime-local" value={form.from} onChange={(e) => set("from", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="to">Hasta</Label>
            <Input id="to" type="datetime-local" value={form.to} onChange={(e) => set("to", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="type">Tipo de evento</Label>
            <Input
              id="type" value={form.eventType} placeholder="checkout.session / payment_intent"
              onChange={(e) => set("eventType", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Estado visual</Label>
            <Select value={form.health} onValueChange={(v) => set("health", v as FormState["health"])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="ok">🟢 Procesado</SelectItem>
                <SelectItem value="pending">🟡 Pendiente / revisión</SelectItem>
                <SelectItem value="error">🔴 Error</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Entrega (HTTP)</Label>
            <Select value={form.delivery} onValueChange={(v) => set("delivery", v as FormState["delivery"])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="ok">2xx / 3xx</SelectItem>
                <SelectItem value="error">Con error (4xx / 5xx)</SelectItem>
                <SelectItem value="retried">Con reintentos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Procesamiento</Label>
            <Select value={form.processing} onValueChange={(v) => set("processing", v as FormState["processing"])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="processed">Procesados</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Reserva</Label>
            <Select value={form.booking} onValueChange={(v) => set("booking", v as FormState["booking"])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="with_booking">Con reserva</SelectItem>
                <SelectItem value="with_bokun">Con reserva en Bókun</SelectItem>
                <SelectItem value="without_bokun">Pago sin Bókun</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Entorno</Label>
            <Select value={form.env || "all"} onValueChange={(v) => set("env", v === "all" ? "" : (v as "sandbox" | "live"))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            <Button onClick={apply}>Aplicar filtros</Button>
            <Button variant="outline" onClick={() => { setForm(EMPTY); setApplied(EMPTY); setPage(0); }}>
              Limpiar
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={exporting}>
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Exportar CSV
            </Button>
          </div>

        </section>

        {counts && (
          <div className="flex flex-wrap gap-4 text-sm">
            <span>Eventos en vista: <strong>{counts.total}</strong></span>
            <span className="text-emerald-600">🟢 {counts.ok}</span>
            <span className="text-amber-600">🟡 {counts.pending}</span>
            <span className="text-red-600">🔴 {counts.error}</span>
          </div>
        )}

        {/* Tabla */}
        <section className="rounded-lg border overflow-x-auto">
          {query.isLoading ? (
            <div className="p-8 flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando eventos…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-muted-foreground text-sm">Sin eventos para estos filtros.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Fecha</th>
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Evento</th>
                  <th className="p-2">Pago / Sesión</th>
                  <th className="p-2">Monto</th>
                  <th className="p-2">HTTP</th>
                  <th className="p-2">Intentos</th>
                  <th className="p-2">Pend.</th>
                  <th className="p-2">Reserva</th>
                  <th className="p-2">Bókun</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.stripe_event_id} className="border-t hover:bg-muted/30">
                    <td className="p-2"><HealthBadge health={r.health} /></td>
                    <td className="p-2 whitespace-nowrap">{fmtDate(r.received_at)}</td>
                    <td className="p-2 font-mono text-xs">{r.event_type ?? "—"}</td>
                    <td className="p-2 font-mono text-xs">{r.stripe_event_id}</td>
                    <td className="p-2 font-mono text-xs">
                      {r.payment_intent_id ?? r.checkout_session_id ?? "—"}
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      {r.amount != null ? formatStripeAmount(r.amount, r.currency ?? "mxn") : "—"}
                    </td>
                    <td className="p-2"><HttpBadge status={r.response_status} /></td>
                    <td className="p-2">{r.delivery_attempts ?? "—"}</td>
                    <td className="p-2">{r.pending_webhooks ?? "—"}</td>
                    <td className="p-2 whitespace-nowrap">
                      {r.booking
                        ? <span className="font-mono text-xs">{r.booking.booking_code} · {r.booking.status}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2 font-mono text-xs">
                      {r.booking?.bokun_confirmation_code ?? r.booking?.bokun_booking_id ?? "—"}
                    </td>
                    <td className="p-2">
                      <Button size="sm" variant="ghost" onClick={() => setOpenId(r.stripe_event_id)}>
                        Detalle <ChevronRight className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">Página {page + 1}</span>
          <Button variant="outline" disabled={!query.data?.hasMore} onClick={() => setPage((p) => p + 1)}>
            Siguiente
          </Button>
        </div>
      </main>

      {/* Detalle del evento */}
      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Detalle del evento</SheetTitle>
          </SheetHeader>
          {detail.isLoading ? (
            <div className="p-4 flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : !detail.data?.event ? (
            <div className="p-4 text-sm text-muted-foreground">Evento no encontrado.</div>
          ) : (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-2">
                <HealthBadge health={detail.data.event.health} />
                <span className="text-xs text-muted-foreground">
                  {detail.data.event.livemode ? "Live" : "Sandbox"} · {detail.data.event.env}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="ID del evento">{detail.data.event.stripe_event_id}</Field>
                <Field label="Tipo">{detail.data.event.event_type}</Field>
                <Field label="Creado en Stripe">{fmtDate(detail.data.event.event_created_at)}</Field>
                <Field label="Recibido">{fmtDate(detail.data.event.received_at)}</Field>
                <Field label="Payment Intent">{detail.data.event.payment_intent_id}</Field>
                <Field label="Checkout Session">{detail.data.event.checkout_session_id}</Field>
                <Field label="Cargo">{detail.data.event.charge_id}</Field>
                <Field label="Monto">
                  {detail.data.event.amount != null
                    ? formatStripeAmount(detail.data.event.amount, detail.data.event.currency ?? "mxn")
                    : "—"}
                </Field>
                <Field label="Cliente">
                  {detail.data.event.customer_name ?? detail.data.event.customer_email ?? "—"}
                </Field>
                <Field label="Estado del objeto">{detail.data.event.status}</Field>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Entrega del webhook</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Endpoint">{detail.data.event.endpoint_path}</Field>
                  <Field label="Código HTTP"><HttpBadge status={detail.data.event.response_status} /></Field>
                  <Field label="Intentos de entrega">{detail.data.event.delivery_attempts}</Field>
                  <Field label="pending_webhooks">{detail.data.event.pending_webhooks}</Field>
                  <Field label="Primer intento">{fmtDate(detail.data.event.first_attempt_at)}</Field>
                  <Field label="Último intento">{fmtDate(detail.data.event.last_attempt_at)}</Field>
                  <Field label="Procesado por Mostrando México">
                    {detail.data.event.processed_at ? `Sí · ${fmtDate(detail.data.event.processed_at)}` : "No"}
                  </Field>
                  <Field label="Respuesta del webhook">
                    <code className="text-xs">{detail.data.event.response_body ?? "—"}</code>
                  </Field>
                </div>
                {detail.data.event.processing_error && (
                  <p className="mt-2 rounded bg-red-500/10 p-2 text-xs text-red-600">
                    Error de procesamiento: {detail.data.event.processing_error}
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Reserva resultante</h3>
                {detail.data.bookings.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Este evento no generó (todavía) una reserva asociada.
                  </p>
                ) : (
                  detail.data.bookings.map((b) => (
                    <div key={b.id} className="rounded border p-3 grid grid-cols-2 gap-3 mb-2">
                      <Field label="Código">{b.booking_code}</Field>
                      <Field label="Estado">{b.status}</Field>
                      <Field label="Tour">{b.tour_title}</Field>
                      <Field label="Fecha de viaje">{b.travel_date}</Field>
                      <Field label="Viajeros">{b.travelers}</Field>
                      <Field label="Total">{b.total != null ? `${b.total} ${b.currency ?? ""}` : "—"}</Field>
                      <Field label="Reserva Bókun">{b.bokun_booking_id}</Field>
                      <Field label="Confirmación Bókun">{b.bokun_confirmation_code}</Field>
                      <Field label="Pagada">{fmtDate(b.paid_at)}</Field>
                      <Field label="Cliente">{b.customer_name ?? b.customer_email}</Field>
                      {b.notes && (
                        <div className="col-span-2">
                          <Field label="Notas">{b.notes}</Field>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {detail.data.relatedEvents.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Cronología de eventos del mismo pago</h3>
                  <ul className="space-y-1 text-xs">
                    {detail.data.relatedEvents.map((e) => (
                      <li key={e.stripe_event_id} className="flex flex-wrap gap-2">
                        <span className="text-muted-foreground">{fmtDate(e.received_at)}</span>
                        <span className="font-mono">{e.event_type}</span>
                        <HttpBadge status={e.response_status} />
                        <span>{e.processed_at ? "procesado" : "pendiente"}</span>
                        <span className="text-muted-foreground">{e.delivery_attempts} intento(s)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {detail.data.payloadJson && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Payload original de Stripe</h3>
                  <pre className="max-h-80 overflow-auto rounded bg-muted p-3 text-xs">
                    {detail.data.payloadJson}
                  </pre>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
